import React, { useState, ReactNode } from 'react';
import classNames from 'classnames';

import {
    SquareCorner,
    SemiCorner,
    RoundedCorner,
    ButtonOutline,
    ButtonSolid,
    ButtonText,
    Email as EmailOutline,
} from '@menuzen/icon/build/outline';
import { Email as EmailSolid } from '@menuzen/icon/build/solid';
import { Email as EmailShape } from '@menuzen/icon/build/shape';
import { ZenTabPane, ZenTabs } from '../../../../components/tabs';
import { useEditor, IconStyle, ButtonStyle, CornerStyle } from '@mnz/builder';
import Dialog, { DialogProps } from '../../../../components/dialog/Dialog';

const tabList = [
    {
        key: 'corners',
        tab: 'Corners',
    },
    {
        key: 'icons',
        tab: 'Icons',
    },
    {
        key: 'buttons',
        tab: 'Buttons',
    },
];

const cornerStyles: { label: string; value: CornerStyle; icon: ReactNode }[] = [
    {
        label: 'Square',
        value: {
            borderRadius: '0',
        },
        icon: <SquareCorner />,
    },
    {
        label: 'Semi',
        value: {
            borderRadius: '0.5em',
        },
        icon: <SemiCorner />,
    },
    {
        label: 'Rounded',
        value: {
            borderRadius: '99999em',
        },
        icon: <RoundedCorner />,
    },
];
const buttonsStyles: { label: string; value: ButtonStyle }[] = [
    {
        label: 'Outline',
        value: 'outline',
    },
    {
        label: 'Solid',
        value: 'solid',
    },
    {
        label: 'Text Only',
        value: 'text-only',
    },
];
const iconStyles: { label: string; value: IconStyle }[] = [
    {
        label: 'Outline',
        value: 'outline',
    },
    {
        label: 'Shape',
        value: 'shape',
    },
    {
        label: 'Solid',
        value: 'solid',
    },
];

const StyleModal: React.FC<DialogProps> = (props) => {
    const [tab, setTab] = useState<'corners' | 'icons' | 'buttons'>('corners');

    const {
        designSettings,
        actions: { setCornerStyle, setIconStyle, setButtonStyle },
    } = useEditor((_, query) => ({
        designSettings: query.getDesignSettings(),
    }));

    const iconStyle = designSettings?.styles?.icon;
    const buttonStyle = designSettings?.styles?.button;
    const borderRadius = designSettings?.styles?.corner?.borderRadius;

    return (
        <Dialog {...props}>
            <div className="mt-20 px-30 pb-40 overflow-hidden">
                <ZenTabs prefixCls={`zen-card-tabs`} className={`mt-18 mb-22`} activeKey={tab} onChange={setTab}>
                    {tabList.map((item) => (
                        <ZenTabPane key={item.key} tab={item.tab} />
                    ))}
                </ZenTabs>
                <div className="flex justify-center items-center space-x-10 overflow-x-auto flex-nowrap">
                    {tab === 'corners' && (
                        <>
                            {cornerStyles.map((corner, idx) => (
                                <div
                                    key={idx}
                                    className={classNames({
                                        'flex flex-col items-center justify-center text-center w-103 h-103 min-w-103 p-10 border rounded-5':
                                            true,
                                        'border-primary-30 text-grey-new': borderRadius !== corner.value.borderRadius,
                                        'border-teal bg-started': borderRadius === corner.value.borderRadius,
                                    })}
                                    onClick={() => setCornerStyle(corner.value)}
                                >
                                    <div className="h-45 w-45 font-bold text-45">{corner.icon}</div>
                                    <p className="font-semibold text-10 leading-13">{corner.label}</p>
                                </div>
                            ))}
                        </>
                    )}
                    {tab === 'buttons' && (
                        <>
                            {buttonsStyles.map((style, idx) => (
                                <div
                                    key={idx}
                                    className={classNames({
                                        'flex flex-col items-center justify-center text-center w-103 h-103 min-w-103 p-10 border rounded-5':
                                            true,
                                        'border-primary-30 text-grey-new': buttonStyle !== style.value,
                                        'border-teal bg-started': buttonStyle === style.value,
                                    })}
                                    onClick={() => setButtonStyle(style.value)}
                                >
                                    <div className="h-45 w-45 font-bold text-45">
                                        {style.value === 'solid' && <ButtonSolid />}
                                        {style.value === 'outline' && <ButtonOutline />}
                                        {style.value === 'text-only' && <ButtonText />}
                                    </div>
                                    <p className="font-semibold text-10 leading-13">{style.label}</p>
                                </div>
                            ))}
                        </>
                    )}
                    {tab === 'icons' && (
                        <>
                            {iconStyles.map((style, idx) => (
                                <div
                                    key={idx}
                                    className={classNames({
                                        'flex flex-col items-center justify-center text-center w-103 h-103 min-w-103 p-10 border rounded-5':
                                            true,
                                        'border-primary-30 text-grey-new': iconStyle !== style.value,
                                        'border-teal bg-started': iconStyle === style.value,
                                    })}
                                    onClick={() => setIconStyle(style.value)}
                                >
                                    <div className="h-45 w-45 font-bold text-45 flex items-center justify-center">
                                        {style.value === 'outline' && <EmailOutline />}
                                        {style.value === 'solid' && <EmailSolid />}
                                        {style.value === 'shape' && (
                                            <div className="text-30 rounded-5 overflow-hidden">
                                                <EmailShape />
                                            </div>
                                        )}
                                    </div>
                                    <p className="font-semibold text-10 leading-13">{style.label}</p>
                                </div>
                            ))}
                        </>
                    )}
                </div>
            </div>
        </Dialog>
    );
};

export default StyleModal;
